import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Search, RotateCcw, RefreshCw, FileSpreadsheet, LayoutGrid, List,
    Check, Users, CalendarRange, Info, AlertCircle, Clock,
    ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';

import Breadcrumb, { BREADCRUMB_EXPORT_BTN } from '../../components/Breadcrumb/Breadcrumb';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import {
    fetchAllAttendanceCard,
    fetchApprovedLeaves,
    fetchHolidays,
} from '../../store/slices/attendanceSlice';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import { fetchLeaveTypes, selectLeaveTypes } from '../../store/slices/leaveTypesSlice';
import { exportAttendanceOverviewToExcel } from '../../utils/attendanceOverviewExcel';

import {
    pad,
    toYMD, todayYMD,
    extractShift, computeDayStatus,
    computeHours, computeOT, formatHoursMinutes,
} from '../../utils/attendanceStatus';

import { createPortal } from 'react-dom';

const FIELD =
    'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-gray-700 transition-shadow';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

/* Raw decimal hours */
const fmtH = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return `${Number(n.toFixed(2))} h`;
};

function attendanceStatusLabel(r) {
    switch (r.status) {
        case 'present': return 'Full Day';
        case 'late': return 'Late';
        case 'short-leave':
            return r.statusReason === 'evening' ? 'Evening Short Leave' : 'Morning Short Leave';
        case 'half-day':
            return r.statusReason === 'morning' ? 'Morning Half Day' : 'Evening Half Day';
        case 'missing-punch': return 'Missing Punch';
        case 'leave':
        case 'half-leave': return 'Leave';
        // case 'sunday': return 'Sunday';
        case 'holiday': return 'Holiday';
        default: return 'No Record';
    }
}

/* Solid hover backgrounds for sticky cells */
const rowBgHover = (r) => {
    switch (r.status) {
        case 'present': return 'group-hover:bg-gray-100';
        case 'late': return 'group-hover:bg-orange-100';
        case 'half-day': return 'group-hover:bg-indigo-100';
        case 'missing-punch': return 'group-hover:bg-fuchsia-100';
        case 'leave': return 'group-hover:bg-blue-200';
        case 'half-leave': return 'group-hover:bg-cyan-100';
        case 'short-leave': return 'group-hover:bg-sky-100';
        case 'holiday': return 'group-hover:bg-yellow-100';
        case 'no-record': return 'group-hover:bg-slate-100';
        default: return 'group-hover:bg-slate-50';
    }
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => String(currentYear + 1 - i));
const MONTH_OPTIONS = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const STATUS_FILTERS = [
    { value: 'all', label: 'All Days' },
    { value: 'full-day', label: 'Full Day' },
    { value: 'late', label: 'Late' },
    { value: 'short-leave-morning', label: 'Morning Short Leave' },
    { value: 'short-leave-evening', label: 'Evening Short Leave' },
    { value: 'half-day-morning', label: 'Morning Half Day' },
    { value: 'half-day-evening', label: 'Evening Half Day' },
    { value: 'missing-punch', label: 'Missing Punch' },
    { value: 'leave', label: 'Leave' },
    // { value: 'sunday', label: 'Sunday' },
    { value: 'holiday', label: 'Holiday' },
    { value: 'no-record', label: 'No Record' },
];

const STATUS_MATCHERS = {
    'full-day': (r) => r.status === 'present',
    'late': (r) => r.status === 'late',
    'short-leave-morning': (r) => r.status === 'short-leave' && r.statusReason === 'morning',
    'short-leave-evening': (r) => r.status === 'short-leave' && r.statusReason === 'evening',
    'half-day-morning': (r) => r.status === 'half-day' && r.statusReason === 'morning',
    'half-day-evening': (r) => r.status === 'half-day' && (r.statusReason === 'evening' || r.statusReason === 'early-out'),
    'missing-punch': (r) => r.status === 'missing-punch',
    'leave': (r) => r.status === 'leave' || r.status === 'half-leave',
    // 'sunday': (r) => r.status === 'sunday',
    'holiday': (r) => r.status === 'holiday',
    'no-record': (r) => r.status === 'no-record',
};


// Column definitions.
const RAW_COLUMNS = [
    { key: 'ServiceNo', label: 'Service No', width: 100, align: 'center', sticky: true },
    { key: 'Name', label: 'Name', width: 240, align: 'left', sticky: true },
    { key: 'AttDate', label: 'Date', width: 120, align: 'center', sticky: true },
    { key: 'dayName', label: 'Day', width: 100, align: 'left', sticky: true },
    { key: 'inTime', label: 'In Time', width: 80, align: 'center', sticky: true },
    { key: 'outTime', label: 'Out Time', width: 90, align: 'center', sticky: true },
    { key: 'shiftName', label: 'Shift Type', width: 140, align: 'left', sticky: true },
    { key: 'status', label: 'Status', width: 140, align: 'center' },
    {
        key: 'totalHours', label: 'Total Hours', width: 120, align: 'center',
        tooltip: 'Total Hours = Normal Hours + Adjusted OT Hours.',
    },
    {
        key: 'normalHours', label: 'Normal Hours', width: 140, align: 'center',
        tooltip: 'Normal Hours — regular working hours inside the standard shift (excludes overtime).',
    },
    {
        key: 'otHours', label: 'OT Hours', width: 100, align: 'center',
        tooltip: 'OT Hours — total overtime hours beyond the standard shift.',
    },
    {
        key: 'othoursRate', label: 'OT Hours Rate', width: 150, align: 'center',
        tooltip: 'OT Hours Rate — multiplier applied to OT hours (e.g. 1.5 × = time-and-a-half, 2.0 × = double).',
    },
    {
        key: 'adjustedOTHours', label: 'Adjusted OT Hours', width: 170, align: 'center',
        tooltip: 'Adjusted OT Hours — OT hours after applying the OT Hours Rate (OT Hours × Rate).',
    },
    { key: 'Department', label: 'Department', width: 140, align: 'left' },
    { key: 'Location', label: 'Location', width: 140, align: 'left' },
    { key: 'EmploymentCategory', label: 'Category', width: 110, align: 'center' },
];

// Accumulate sticky offsets from widths — single source of truth.
const COLUMNS = (() => {
    let cursor = 0;
    return RAW_COLUMNS.map((c) => {
        if (!c.sticky) return c;
        const withLeft = { ...c, left: cursor };
        cursor += c.width;
        return withLeft;
    });
})();

// Quick lookup for the body / footer cells.
const STICKY_LEFT = Object.fromEntries(
    COLUMNS.filter((c) => c.sticky).map((c) => [c.key, c.left])
);

const TABLE_MIN_WIDTH = COLUMNS.reduce((s, c) => s + c.width, 0);
const STICKY_TOP = -24;
const STICKY_BOTTOM = -22;


function buildMonthDays(year, month) {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const daysInMonth = new Date(y, m, 0).getDate();
    const list = [];
    for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(y, m - 1, d);
        const dow = date.getDay();
        list.push({
            day: d,
            dayName: DAY_NAMES_FULL[dow],
            isSunday: dow === 0,
            ymd: `${y}-${pad(m)}-${pad(d)}`,
        });
    }
    return list;
}

function HeaderTip({ children }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);

    const show = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const w = 224;   // w-56 = 14rem = 224px
        const pad = 8;
        const left = Math.min(
            Math.max(rect.left + rect.width / 2 - w / 2, pad),
            window.innerWidth - w - pad
        );
        setCoords({ top: rect.bottom + 8, left });
        setOpen(true);
    };
    const hide = () => setOpen(false);

    return (
        <>
            <span
                ref={triggerRef}
                className="inline-flex items-center"
                onMouseEnter={show}
                onMouseLeave={hide}
                onClick={(e) => e.stopPropagation()}
            >
                <Info size={11} className="text-slate-400 hover:text-slate-600 cursor-help" />
            </span>
            {open && createPortal(
                <span
                    role="tooltip"
                    className="pointer-events-none fixed z-[9999] w-56
                               rounded-lg bg-slate-900 text-white text-[11px] leading-snug font-normal
                               px-3 py-2 shadow-lg normal-case tracking-normal text-left"
                    style={{ top: coords.top, left: coords.left }}
                >
                    {children}
                    <span
                        className="absolute w-2 h-2 bg-slate-900"
                        style={{ top: -4, left: '50%', transform: 'translateX(-50%) rotate(45deg)' }}
                    />
                </span>,
                document.body
            )}
        </>
    );
}

export default function AttendanceOverview() {
    // ─── Router / dispatch ─────────────────────────────────────────
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // ─── Tab flags ─────────────────────────────────────────────────
    const isSummaryTab =
        location.pathname === '/hrm/attendance/details' ||
        location.pathname === '/hrm/attendance/summary';
    const isDetailsTab = location.pathname === '/hrm/attendance/attendance-details';
    const isOverviewTab = location.pathname === '/hrm/attendance/attendance-overview';

    // ─── Selectors ─────────────────────────────────────────────────
    const { rows, leaves, holidays, loading, error } = useSelector(
        (s) => s.attendance || { rows: [], leaves: [], holidays: [], loading: false, error: null }
    );
    const { employees: reduxEmployees } = useSelector(
        (s) => s.employees || { employees: [] }
    );
    const leaveTypes = useSelector(selectLeaveTypes);

    // ─── Local state ───────────────────────────────────────────────
    const [selectedMonth, setSelectedMonth] = useState(() => pad(new Date().getMonth() + 1));
    const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [sort, setSort] = useState({ key: 'AttDate', dir: 'asc' });
    const [refreshing, setRefreshing] = useState(false);

    // ─── Scroll-sync refs & state ──────────────────────────────────
    const tableScrollRef = useRef(null);
    const barRef = useRef(null);
    const headerRef = useRef(null);
    const syncing = useRef(false);
    const tableRafRef = useRef(0);
    const barRafRef = useRef(0);
    const filterDebounceRef = useRef(null);
    const isFirstFetchRef = useRef(true);
    const [bar, setBar] = useState({ scrollWidth: 0, hasOverflow: false });

    const measureBar = useCallback(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        const next = {
            scrollWidth: el.scrollWidth,
            hasOverflow: el.scrollWidth > el.clientWidth + 1,
        };
        setBar((prev) =>
            prev.scrollWidth === next.scrollWidth && prev.hasOverflow === next.hasOverflow
                ? prev : next
        );
    }, []);

    // ─── Lookup maps ───────────────────────────────────────────────
    const employeeMap = useMemo(() => {
        const map = new Map();
        if (!Array.isArray(reduxEmployees)) return map;
        reduxEmployees.forEach((emp) => {
            const serviceNo = String(
                emp.HED_SERVICE_NO || emp.SERVICE_NO || emp.serviceNo || emp.HED_EMPLOYEE_ID || ''
            ).trim();
            const employeeId = String(emp.HED_EMPLOYEE_ID || emp.EMPLOYEE_ID || emp.employeeId || '').trim();

            const rec = {
                employeeId,
                serviceNo,
                name: emp.HED_REPORT_NAME ||
                    `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() ||
                    `Employee ${serviceNo}`,
                department: emp.HDD_DEPARTMENT_NAME || emp.HED_DEPARTMENT_NAME || '',
                location: emp.HED_LOCATION_NAME || emp.HDL_LOCATION_NAME ||
                    emp.LOCATION_NAME || emp.Location || '',
                designation: emp.HED_DESIGNATION_NAME || '',
                category: emp.HED_EMPLOYMENT_CATEGORY || emp.EMPLOYMENT_CATEGORY || '',

                shiftId: emp.HED_SHIFT_ID ?? null,
                shiftCode: emp.HSD_SHIFT_CODE ?? null,
                shiftName: emp.HSD_SHIFT_NAME ?? null,
                shiftStartTime: emp.HSD_START_TIME ?? null,
                shiftEndTime: emp.HSD_END_TIME ?? null,
            };
            if (serviceNo) map.set(serviceNo, rec);
            if (employeeId && !map.has(employeeId)) map.set(employeeId, rec);
        });
        return map;
    }, [reduxEmployees]);

    const leaveTypeMap = useMemo(() => {
        const map = new Map();
        (leaveTypes || []).forEach((t) => map.set(String(t.id), t.name || t.code || `Type ${t.id}`));
        return map;
    }, [leaveTypes]);

    const resolveLeaveType = useCallback(
        (rawId) => leaveTypeMap.get(String(rawId)) || `Leave (${rawId})`,
        [leaveTypeMap]
    );

    const holidayMap = useMemo(() => {
        const map = new Map();
        holidays.forEach((h) => {
            if (h?.HAH_IS_ACTIVE === 'False') return;
            const ymd = toYMD(h.HAH_HOLIDAY_DATE);
            if (ymd) map.set(ymd, {
                name: h.HAH_HOLIDAY_NAME || 'Holiday',
                type: h.HAH_HOLIDAY_TYPE || '',
            });
        });
        return map;
    }, [holidays]);

    const leaveMap = useMemo(() => {
        const map = new Map();
        (leaves || []).forEach((l) => {
            const serviceNo = String(l.HLF_EMP_SERVICE_NO || '').trim();
            if (!serviceNo) return;
            const from = new Date(l.HLF_FROM_DATE);
            const to = new Date(l.HLF_TO_DATE);
            if (isNaN(from.getTime()) || isNaN(to.getTime())) return;

            const totalDays = parseFloat(l.HLF_TOTAL_DAYS || '0');
            const isHalf = l.HLF_HALF_DAY === 'Y' || totalDays === 0.5;

            const cursor = new Date(from); cursor.setHours(0, 0, 0, 0);
            const end = new Date(to); end.setHours(0, 0, 0, 0);
            while (cursor <= end) {
                const ymd = toYMD(cursor);
                if (ymd) map.set(`${serviceNo}|${ymd}`, {
                    leaveTypeId: l.HLF_LEAVE_TYPE_ID,
                    leaveTypeName: resolveLeaveType(l.HLF_LEAVE_TYPE_ID),
                    isHalf,
                    days: totalDays,
                    reason: l.HLF_REASON || '',
                });
                cursor.setDate(cursor.getDate() + 1);
            }
        });
        return map;
    }, [leaves, resolveLeaveType]);

    const attendanceMap = useMemo(() => {
        const map = new Map();
        (rows || []).forEach((r) => {
            const serviceNo = String(r.ServiceNo ?? '').trim();
            const ymd = toYMD(r.AttDate);
            if (serviceNo && ymd) map.set(`${serviceNo}|${ymd}`, r);
        });
        return map;
    }, [rows]);

    const todayStr = useMemo(() => todayYMD(), []);

    const monthDays = useMemo(() => {
        const all = buildMonthDays(selectedYear, selectedMonth);

        const now = new Date();
        const curYear = now.getFullYear();
        const curMonth = now.getMonth() + 1;
        const curDay = now.getDate();

        const isCurrentMonth =
            Number(selectedYear) === curYear && Number(selectedMonth) === curMonth;

        if (!isCurrentMonth) return all;
        return all.filter((d) => d.day <= curDay);
    }, [selectedYear, selectedMonth]);

    // ─── Derived employee lists ───────────────────────────────────
    const allEmployees = useMemo(() => {
        const seen = new Set();
        const out = [];
        (rows || []).forEach((r) => {
            const serviceNo = String(r.ServiceNo ?? '').trim();
            if (!serviceNo || seen.has(serviceNo)) return;
            seen.add(serviceNo);

            const master = employeeMap.get(serviceNo) || {};
            out.push({
                serviceNo,
                name: r.Name || master.name || `Employee ${serviceNo}`,
                department: r.Department || master.department || '',
                location: r.Location || master.location || '',
                category: r.EmploymentCategory || master.category || '',
                designation: r.Designation || master.designation || '',

                shiftId: master.shiftId ?? null,
                shiftCode: master.shiftCode ?? null,
                shiftName: master.shiftName ?? null,
                shiftStartTime: master.shiftStartTime ?? null,
                shiftEndTime: master.shiftEndTime ?? null,
            });
        });
        return out.sort((a, b) => a.name.localeCompare(b.name));
    }, [rows, employeeMap]);

    const employeeOptions = useMemo(() => {
        return allEmployees.map((e) => ({
            employeeId: e.serviceNo,
            serviceNo: e.serviceNo,
            name: e.name,
            department: e.department,
            location: e.location,
            designation: e.designation,
            category: e.category,
        }));
    }, [allEmployees]);

    // ─── Row derivation ───────────────────────────────────────────
    const flatRows = useMemo(() => {
        if (!hasSearched) return [];

        const empFilterId = selectedEmployee && !selectedEmployee.isAll
            ? String(selectedEmployee.serviceNo || selectedEmployee.employeeId || '').trim()
            : null;

        const out = [];
        for (const emp of allEmployees) {
            if (empFilterId && emp.serviceNo !== empFilterId) continue;

            const employeeShift = extractShift(emp);

            for (const d of monthDays) {
                const key = `${emp.serviceNo}|${d.ymd}`;
                const att = attendanceMap.get(key);
                const leave = leaveMap.get(key);
                const holiday = holidayMap.get(d.ymd);
                const isToday = d.ymd === todayStr;

                const inTime = att?.InTime || '';
                const outTime = att?.OutTime || '';
                const totalHours = att?.TotalHours != null
                    ? Number(att.TotalHours)
                    : (inTime && outTime ? computeHours(inTime, outTime) : 0);
                const normalHours = att?.NormalHours != null
                    ? Number(att.NormalHours)
                    : (totalHours > 0 ? Math.min(totalHours, 8) : 0);
                const otHours = att?.OTHours != null
                    ? Number(att.OTHours)
                    : (inTime && outTime ? computeOT(inTime, outTime) : 0);
                const preOT = att?.PreShiftOTHours != null ? Number(att.PreShiftOTHours) : 0;
                const postOT = att?.PostOTHours != null ? Number(att.PostOTHours) : 0;
                const othoursRate = att?.OTHoursRate != null ? Number(att.OTHoursRate) : 0;
                const adjustedOTHours = att?.AdjustedOTHours != null ? Number(att.AdjustedOTHours) : 0;

                const { status, reason: statusReason, missingIn, missingOut } = computeDayStatus({
                    date: d.ymd,
                    attendance: att,
                    leave,
                    holiday,
                    shift: employeeShift,
                });

                if (statusFilter !== 'all') {
                    const match = STATUS_MATCHERS[statusFilter];
                    if (match && !match({ status, statusReason })) continue;
                }

                out.push({
                    ...emp,
                    ...d,
                    att, leave, holiday, isToday,
                    inTime, outTime, totalHours, normalHours, otHours, preOT, postOT,
                    othoursRate, adjustedOTHours,
                    status, statusReason, missingIn, missingOut,
                    AttDate: d.ymd,
                    Name: att?.Name || emp.name,
                    ServiceNo: emp.serviceNo,
                    Department: att?.Department || emp.department || '',
                    Location: att?.Location || emp.location || '',
                    EmploymentCategory: att?.EmploymentCategory || emp.category || '',
                    Designation: att?.Designation || emp.designation || '',
                    shiftName: emp.shiftName || '—',
                });
            }
        }
        return out;
    }, [
        hasSearched, allEmployees, monthDays, attendanceMap, leaveMap, holidayMap,
        statusFilter, selectedEmployee, todayStr,
    ]);

    const sortedRows = useMemo(() => {
        const list = [...flatRows];
        const { key, dir } = sort;
        const mult = dir === 'asc' ? 1 : -1;
        list.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
            return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
        });
        return list;
    }, [flatRows, sort]);

    const totals = useMemo(() => {
        return sortedRows.reduce(
            (acc, r) => {
                acc.totalHours += Number(r.totalHours) || 0;
                acc.normalHours += Number(r.normalHours) || 0;
                acc.otHours += Number(r.otHours) || 0;
                acc.othoursRate += Number(r.othoursRate) || 0;
                acc.adjustedOTHours += Number(r.adjustedOTHours) || 0;
                return acc;
            },
            { totalHours: 0, normalHours: 0, otHours: 0, othoursRate: 0, adjustedOTHours: 0 }
        );
    }, [sortedRows]);

    // ─── Scroll-sync handlers (rAF-throttled) ─────────────────────
    const onTableScroll = () => {
        if (tableRafRef.current) return;
        tableRafRef.current = requestAnimationFrame(() => {
            tableRafRef.current = 0;
            if (headerRef.current && tableScrollRef.current) {
                headerRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
            }
            if (syncing.current) { syncing.current = false; return; }
            if (barRef.current && tableScrollRef.current) {
                syncing.current = true;
                barRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
            }
        });
    };

    const onBarScroll = () => {
        if (barRafRef.current) return;
        barRafRef.current = requestAnimationFrame(() => {
            barRafRef.current = 0;
            if (syncing.current) { syncing.current = false; return; }
            if (barRef.current && tableScrollRef.current) {
                syncing.current = true;
                tableScrollRef.current.scrollLeft = barRef.current.scrollLeft;
            }
        });
    };

    // ─── Action callbacks ─────────────────────────────────────────
    const handleSearch = useCallback(() => {
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
            filterDebounceRef.current = null;
        }
        const monthParam = `${selectedYear}-${selectedMonth}`;
        setHasSearched(true);
        dispatch(fetchAllAttendanceCard({ month: monthParam }));
        dispatch(fetchApprovedLeaves({}));
    }, [selectedYear, selectedMonth, dispatch]);

    const handleRefresh = useCallback(async () => {
        if (refreshing || loading) return;
        setRefreshing(true);
        try {
            const monthParam = `${selectedYear}-${selectedMonth}`;
            setHasSearched(true);
            await Promise.all([
                dispatch(fetchAllAttendanceCard({ month: monthParam })).unwrap(),
                dispatch(fetchApprovedLeaves({})).unwrap(),
                dispatch(fetchHolidays()).unwrap(),
                dispatch(fetchLeaveTypes()).unwrap(),
            ]);
        } catch { /* slice already holds error */ }
        finally { setRefreshing(false); }
    }, [dispatch, refreshing, loading, selectedYear, selectedMonth]);

    const handleReset = useCallback(() => {
        const today = new Date();
        setSelectedYear(String(today.getFullYear()));
        setSelectedMonth(pad(today.getMonth() + 1));
        setStatusFilter('all');
        setSelectedEmployee(null);
        setHasSearched(false);
        setSort({ key: 'AttDate', dir: 'desc' });
    }, []);

    const handleTabChange = useCallback((tab) => {
        if (tab === 'summary') navigate('/hrm/attendance/details');
        else if (tab === 'details') navigate('/hrm/attendance/attendance-details');
        else if (tab === 'overview') navigate('/hrm/attendance/attendance-overview');
    }, [navigate]);

    const toggleDateSort = useCallback(() => {
        setSort((s) => ({
            key: 'AttDate',
            dir: s.key === 'AttDate' && s.dir === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    const isDateActive = sort.key === 'AttDate';

    const handleExport = useCallback(() => {
        if (!sortedRows.length) return;
        exportAttendanceOverviewToExcel({
            rows: sortedRows.map((r) => ({
                ServiceNo: r.ServiceNo,
                Name: r.Name,
                Date: r.AttDate,
                Day: r.dayName,
                InTime: r.inTime,
                OutTime: r.outTime,
                shiftName: r.shiftName,
                TotalHours: r.totalHours,
                NormalHours: r.normalHours,
                OTHours: r.otHours,
                OTHoursRate: r.othoursRate,
                AdjustedOTHours: r.adjustedOTHours,
                Status: attendanceStatusLabel(r),
                Department: r.Department,
                Location: r.Location,
                Category: r.EmploymentCategory,
            })),
            filters: {
                Year: selectedYear,
                Month: selectedMonth,
                Show: STATUS_FILTERS.find((f) => f.value === statusFilter)?.label || statusFilter,
                Employee:
                    selectedEmployee && !selectedEmployee.isAll
                        ? `${selectedEmployee.serviceNo || ''} — ${selectedEmployee.name || ''}`.trim()
                        : 'All Employees',
                'Total Records': sortedRows.length,
            },
        });
    }, [sortedRows, selectedYear, selectedMonth, selectedEmployee, statusFilter]);

    // ─── Effects ──────────────────────────────────────────────────
    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return undefined;
        measureBar();
        const ro = new ResizeObserver(measureBar);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild);
        window.addEventListener('resize', measureBar);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measureBar);
        };
    }, [measureBar, sortedRows.length]);

    useEffect(() => {
        dispatch(fetchAllEmployees());
        dispatch(fetchHolidays());
        dispatch(fetchLeaveTypes());
    }, [dispatch]);

    useEffect(() => {
        if (!selectedYear || !selectedMonth) return undefined;
        if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);

        const delay = isFirstFetchRef.current ? 0 : 350;
        filterDebounceRef.current = setTimeout(() => {
            isFirstFetchRef.current = false;
            filterDebounceRef.current = null;
            const monthParam = `${selectedYear}-${selectedMonth}`;
            setHasSearched(true);
            dispatch(fetchAllAttendanceCard({ month: monthParam }));
            dispatch(fetchApprovedLeaves({}));
        }, delay);

        return () => {
            if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
        };
    }, [selectedYear, selectedMonth, dispatch]);

    // ─── Row backgrounds ──────────────────────────────────────────
    const rowClass = (r) => {
        switch (r.status) {
            case 'present': return 'bg-white hover:bg-gray-100';
            case 'late': return 'bg-orange-50 hover:bg-orange-100';
            case 'half-day': return 'bg-indigo-50 hover:bg-indigo-100';
            case 'missing-punch': return 'bg-fuchsia-50 hover:bg-fuchsia-100';
            case 'leave': return 'bg-blue-100 hover:bg-blue-200';
            case 'half-leave': return 'bg-cyan-50 hover:bg-cyan-100';
            case 'short-leave': return 'bg-sky-50 hover:bg-sky-100';
            case 'holiday': return 'bg-yellow-100 hover:bg-yellow-100';
            case 'no-record': return 'bg-slate-50 hover:bg-slate-100';
            default: return 'bg-white hover:bg-slate-50';
        }
    };

    const rowBgSolid = (r) => {
        switch (r.status) {
            case 'present': return 'bg-white';
            case 'late': return 'bg-orange-50';
            case 'half-day': return 'bg-indigo-50';
            case 'missing-punch': return 'bg-fuchsia-50';
            case 'leave': return 'bg-blue-100';
            case 'half-leave': return 'bg-cyan-50';
            case 'short-leave': return 'bg-sky-50';
            case 'holiday': return 'bg-yellow-100';
            case 'no-record': return 'bg-slate-50';
            default: return 'bg-white';
        }
    };

    const statusBadge = (r) => {
        const label = attendanceStatusLabel(r);
        switch (r.status) {
            case 'present':
                return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-400">
                        {label}
                    </span>
                );
            case 'late':
                return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-400">
                        Late
                    </span>
                );
            case 'short-leave':
                return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-400">
                        {label}
                    </span>
                );
            case 'half-day':
                return (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-400">
                        {label}
                    </span>
                );
            case 'missing-punch':
                return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-fuchsia-50 text-fuchsia-700 ring-1 ring-inset ring-fuchsia-400">
                        Missing Punch
                    </span>
                );
            case 'leave':
                return (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-400">
                        {label}
                    </span>
                );
            case 'half-leave':
                return (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-400">
                        {label}
                    </span>
                );
            case 'holiday':
                return (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-yellow-100 text-black ring-1 ring-inset ring-yellow-600">
                        {label}
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-300">
                        {label}
                    </span>
                );
        }
    };
    // ─── Header cell ──────────────────────────────────────────────
    const SortHeader = ({ colKey, label, align = 'left', width, tooltip, sticky, left }) => {
        const active = sort.key === colKey;
        const arrow = active ? (sort.dir === 'asc' ? '▲' : '▼') : '';
        return (
            <th
                onClick={() => setSort((s) => ({
                    key: colKey,
                    dir: s.key === colKey && s.dir === 'asc' ? 'desc' : 'asc',
                }))}
                style={{ width, ...(sticky ? { position: 'sticky', left } : {}) }}
                className={`px-3 py-2.5 font-semibold border-b border-slate-200 bg-slate-50 cursor-pointer select-none
                ${sticky ? 'z-40' : ''}
                ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
            >
                <span className="inline-flex items-center gap-1">
                    {label}
                    {tooltip && <HeaderTip>{tooltip}</HeaderTip>}
                    <span className="text-[8px] text-slate-400">{arrow}</span>
                </span>
            </th>
        );
    };

    // Reusable style for a sticky body / footer cell
    const stickyTdStyle = (left) => ({ position: 'sticky', left, zIndex: 10 });

    return (
        <div className="p-4 md:p-0 space-y-2 bg-slate-50/40 min-h-screen">
            <Breadcrumb
                trail={['eHRM', 'Attendance & Shifts', 'Attendance Overview']}
                title="Attendance Overview"
                action={
                    <button onClick={handleExport} disabled={!sortedRows.length || loading}
                        className={BREADCRUMB_EXPORT_BTN}>
                        <FileSpreadsheet size={15} /> Export to Excel
                    </button>
                }
            />

            {/* Tabs */}
            <div className="border-b border-slate-200 bg-white rounded-t-xl px-4 pt-1 shadow-sm">
                <nav className="flex gap-0" aria-label="Tabs">
                    <button onClick={() => handleTabChange('summary')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isSummaryTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <LayoutGrid size={16} /> Attendance Summary
                    </button>
                    <button onClick={() => handleTabChange('details')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isDetailsTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <List size={16} /> Attendance Details
                    </button>
                    <button onClick={() => handleTabChange('overview')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isOverviewTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <Users size={16} /> Attendance Overview
                    </button>
                </nav>
            </div>

            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-end gap-3">
                    <div className="flex items-end gap-3 md:w-2/5">
                        <div className="flex-1">
                            <span className={LABEL}>Year</span>
                            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className={FIELD}>
                                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div className="flex-1">
                            <span className={LABEL}>Month</span>
                            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className={FIELD}>
                                {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        <div className="flex-1">
                            <span className={LABEL}>Show</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={FIELD}>
                                {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-end gap-2 md:flex-1 md:justify-end">
                        <div className="flex-1 min-w-[220px] max-w-md">
                            <span className={LABEL}>Employee</span>
                            <EmployeeSearch
                                value={selectedEmployee}
                                onSelect={setSelectedEmployee}
                                employees={employeeOptions}
                                loading={loading}
                                showAllOption
                            />
                        </div>
                        <button onClick={handleSearch} disabled={loading}
                            className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 whitespace-nowrap shadow-sm">
                            <Search size={14} /> {loading ? 'Loading...' : 'Search'}
                        </button>
                        <button onClick={handleReset}
                            className="flex items-center gap-2 h-9 px-3 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap">
                            <RotateCcw size={14} /> Reset
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing || loading}
                            title="Refresh data"
                            className="flex items-center gap-2 h-9 px-3 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

            {/* Calendar / Table */}
            <div className="p-4 md:p-0 space-y-2 bg-slate-50/40 min-h-screen">
                <div className="bg-white border border-slate-200 shadow-sm rounded-xl">

                    {/* Card title + Date toggle */}
                    <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-4 rounded-t-xl bg-white">
                        <div className="flex items-center gap-2 text-slate-800">
                            <CalendarRange size={16} className="text-primary-600" />
                            <h3 className="text-sm font-semibold">Attendance Overview</h3>
                        </div>

                        <button
                            type="button"
                            onClick={toggleDateSort}
                            title={
                                isDateActive && sort.dir === 'asc'
                                    ? 'Sorted oldest → newest. Click for newest → oldest.'
                                    : 'Sorted newest → oldest. Click for oldest → newest.'
                            }
                            className={`ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-all active:scale-[0.98] ${isDateActive
                                ? 'border-primary-200 bg-primary-50 text-primary-700 shadow-sm hover:bg-primary-100'
                                : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            {isDateActive ? (
                                sort.dir === 'asc'
                                    ? <ArrowUp size={13} />
                                    : <ArrowDown size={13} />
                            ) : (
                                <ArrowUpDown size={13} className="text-slate-400" />
                            )}
                            <span className="tracking-tight">Date</span>
                            {isDateActive && (
                                <span className="ml-0.5 text-[10px] font-bold tracking-widest opacity-60">
                                    {sort.dir === 'asc' ? 'ASC' : 'DESC'}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* ─── STICKY HEADER ─── */}
                    <div
                        ref={headerRef}
                        className="sticky z-30 overflow-hidden bg-slate-50"
                        style={{ top: STICKY_TOP }}
                    >
                        <table
                            className="border-separate border-spacing-0 text-xs"
                            style={{ width: '100%', minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}
                        >
                            <colgroup>
                                {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
                            </colgroup>
                            <thead>
                                <tr className="bg-slate-50 text-slate-500">
                                    {COLUMNS.map((c) => (
                                        <SortHeader
                                            key={c.key}
                                            colKey={c.key}
                                            label={c.label}
                                            width={c.width}
                                            align={c.align}
                                            tooltip={c.tooltip}
                                            sticky={c.sticky}
                                            left={c.left}
                                        />
                                    ))}
                                </tr>
                            </thead>
                        </table>
                    </div>

                    {/* ─── BODY ─── */}
                    <div
                        ref={tableScrollRef}
                        onScroll={onTableScroll}
                        className="relative overflow-x-auto [&::-webkit-scrollbar]:hidden"
                        style={{ scrollbarWidth: 'none' }}
                    >
                        <table
                            className="border-separate border-spacing-0 text-xs"
                            style={{ width: '100%', minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}
                        >
                            <colgroup>
                                {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
                            </colgroup>
                            <tbody>
                                {sortedRows.map((r) => (
                                    <tr key={`${r.ServiceNo}|${r.AttDate}`} className={`group ${rowClass(r)}`}>
                                        {/* Service No */}
                                        <td
                                            style={stickyTdStyle(STICKY_LEFT.ServiceNo)}
                                            className={`px-3 py-2 text-center text-slate-500 border-b border-slate-100 tabular-nums ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.ServiceNo}
                                        </td>

                                        {/* Name */}
                                        <td
                                            style={stickyTdStyle(STICKY_LEFT.Name)}
                                            className={`px-3 py-2 text-slate-900 font-medium border-b border-slate-100 truncate ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.Name}
                                        </td>

                                        {/* Date */}
                                        <td
                                            style={stickyTdStyle(STICKY_LEFT.AttDate)}
                                            className={`px-3 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums font-medium ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.AttDate}
                                        </td>

                                        {/* Day */}
                                        <td
                                            style={stickyTdStyle(STICKY_LEFT.dayName)}
                                            className={`px-3 py-2 border-b border-slate-100 ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            <span className={`font-semibold ${r.isSunday ? 'text-rose-600' : 'text-slate-600'}`}>{r.dayName}</span>
                                        </td>

                                        {/* In Time */}
                                        <td
                                            style={stickyTdStyle(STICKY_LEFT.inTime)}
                                            className={`px-3 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.inTime || '—'}
                                        </td>

                                        {/* Out Time */}
                                        <td
                                            style={stickyTdStyle(STICKY_LEFT.outTime)}
                                            className={`px-3 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.outTime || '—'}
                                        </td>

                                        {/* Shift Type — last sticky column, no shadow, no border */}
                                        <td
                                            style={stickyTdStyle(STICKY_LEFT.shiftName)}
                                            className={`px-3 py-2 text-slate-600 border-b border-slate-100 truncate ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                            title={r.shiftName}
                                        >
                                            {r.shiftName || '—'}
                                        </td>

                                        <td className="px-3 py-2 text-center border-b border-slate-100">{statusBadge(r)}</td>

                                        {/* Total Hours */}
                                        <td className="px-3 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtH(r.totalHours)}
                                        </td>

                                        {/* Normal Hours */}
                                        <td className="px-3 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtH(r.normalHours)}
                                        </td>

                                        {/* OT Hours */}
                                        <td className="px-3 py-2 text-center border-b border-slate-100 tabular-nums">
                                            {r.otHours > 0
                                                ? <span className="font-semibold text-emerald-700">{fmtH(r.otHours)}</span>
                                                : <span className="text-slate-400">—</span>}
                                        </td>

                                        {/* OT Hours Rate */}
                                        <td className="px-3 py-2 text-center border-b border-slate-100 tabular-nums text-slate-700">
                                            {r.othoursRate > 0 ? r.othoursRate.toFixed(2) : <span className="text-slate-400">—</span>}
                                        </td>

                                        {/* Adjusted OT Hours */}
                                        <td className="px-3 py-2 text-center border-b border-slate-100 tabular-nums text-slate-700">
                                            {r.adjustedOTHours > 0 ? fmtH(r.adjustedOTHours) : <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-slate-600 border-b border-slate-100 truncate">{r.Department || '—'}</td>
                                        <td className="px-3 py-2 text-slate-600 border-b border-slate-100 truncate">{r.Location || '—'}</td>
                                        <td className="px-3 py-2 text-center text-slate-600 border-b border-slate-100 truncate">{r.EmploymentCategory || '—'}</td>
                                    </tr>
                                ))}

                                {sortedRows.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-slate-400">
                                            {loading ? 'Loading attendance data…'
                                                : hasSearched
                                                    ? 'No records match the current filters.'
                                                    : 'Pick a month and click Search.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            {/* ─── TOTALS FOOTER ─── */}
                            {selectedEmployee && !selectedEmployee.isAll && sortedRows.length > 0 && (
                                <tfoot>
                                    <tr className="bg-slate-100 text-slate-800 font-semibold shadow-[0_-2px_0_0_rgb(203_213_225)]">

                                        {/* 7 sticky empty cells — offsets from STICKY_LEFT */}
                                        <td style={stickyTdStyle(STICKY_LEFT.ServiceNo)} className="bg-slate-100 px-3 py-2.5 border-t-2 border-slate-300" />
                                        <td style={stickyTdStyle(STICKY_LEFT.Name)} className="bg-slate-100 px-3 py-2.5 border-t-2 border-slate-300" />
                                        <td style={stickyTdStyle(STICKY_LEFT.AttDate)} className="bg-slate-100 px-3 py-2.5 border-t-2 border-slate-300" />
                                        <td style={stickyTdStyle(STICKY_LEFT.dayName)} className="bg-slate-100 px-3 py-2.5 border-t-2 border-slate-300" />
                                        <td style={stickyTdStyle(STICKY_LEFT.inTime)} className="bg-slate-100 px-3 py-2.5 border-t-2 border-slate-300" />
                                        <td style={stickyTdStyle(STICKY_LEFT.outTime)} className="bg-slate-100 px-3 py-2.5 border-t-2 border-slate-300" />
                                        <td style={stickyTdStyle(STICKY_LEFT.shiftName)} className="bg-slate-100 px-3 py-2.5 border-t-2 border-slate-300" />

                                        {/* Status (empty) */}
                                        <td className="border-t-2 border-slate-300 bg-slate-100" />

                                        {/* Total Hours */}
                                        <td className="px-3 py-2.5 text-center tabular-nums border-t-2 border-slate-300 bg-slate-100">
                                            {fmtH(totals.totalHours)}
                                        </td>

                                        {/* Normal Hours */}
                                        <td className="px-3 py-2.5 text-center tabular-nums border-t-2 border-slate-300 bg-slate-100">
                                            {fmtH(totals.normalHours)}
                                        </td>

                                        {/* OT Hours */}
                                        <td className="px-3 py-2.5 text-center tabular-nums border-t-2 border-slate-300 bg-slate-100">
                                            {totals.otHours > 0
                                                ? <span className="text-emerald-700">{fmtH(totals.otHours)}</span>
                                                : '—'}
                                        </td>

                                        {/* OT Hours Rate (blank) */}
                                        <td className="px-3 py-2.5 text-center tabular-nums border-t-2 border-slate-300 bg-slate-100" />

                                        {/* Adjusted OT Hours */}
                                        <td className="px-3 py-2.5 text-center tabular-nums border-t-2 border-slate-300 bg-slate-100">
                                            {fmtH(totals.adjustedOTHours)}
                                        </td>

                                        {/* Department / Location / Category */}
                                        <td colSpan={3} className="border-t-2 border-slate-300 bg-slate-100" />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    {/* ─── STICKY BOTTOM SCROLLBAR ─── */}
                    <div
                        ref={barRef}
                        onScroll={onBarScroll}
                        className="sticky z-30 overflow-x-auto overflow-y-hidden bg-white/95 border-t border-slate-200 rounded-b-xl"
                        style={{
                            bottom: STICKY_BOTTOM,
                            height: 16,
                            display: bar.hasOverflow ? 'block' : 'none',
                        }}
                        aria-hidden="true"
                    >
                        <div style={{ width: bar.scrollWidth, height: 1 }} />
                    </div>
                </div>
            </div>
        </div>
    );
}